import { envConfig } from '#constants/envConfig';

/**
 * Routes Sentry/GlitchTip events to the right project by platform × environment.
 *
 * One project per (platform, env) on sentry.teamplay.gg (org "teamplay"), instead of
 * everything landing in the single `teamplay-frontend` project:
 *   web-dev / web-prod        — the browser build
 *   android-dev / android-prod — the Capacitor shell on Android
 *   ios-dev / ios-prod         — the Capacitor shell on iOS
 *
 * DSNs are public ingest keys (they ship in the client bundle regardless), so they're
 * safe to keep in source — that also makes routing work across every build/deploy path
 * without threading six env vars through CI.
 */
const DSN = {
  webDev: 'https://60611801f3f94f66bc5f0c7312886db1@sentry.teamplay.gg/2',
  webProd: 'https://39097be5eb27469a90fa8c951941bb22@sentry.teamplay.gg/3',
  androidDev: 'https://7d0189e0827c4f6bbe2bc539d3324f89@sentry.teamplay.gg/4',
  androidProd: 'https://038f83fcc9644ea182a894c111dc8dab@sentry.teamplay.gg/5',
  iosDev: 'https://20931329f66f41fd8fca6431ec1308f7@sentry.teamplay.gg/6',
  iosProd: 'https://47f54c85d4054dcdbbb53c3a75919d8b@sentry.teamplay.gg/7',
} as const;

// Reuse the existing, safely-computed dev/prod signal. Do NOT re-derive this by calling
// `.includes()` on an inlined env var — the minifier miscompiled that into `(void 0)(...)`
// and 500'd every SSR route (see envConfig.SENTRY_ENVIRONMENT).
const isDev = (): boolean => envConfig.SENTRY_ENVIRONMENT === 'development';

// Capacitor exposes a global in the native WebView; read the platform synchronously.
// Only ever reached in native builds (guarded by IS_NATIVE), so it tree-shakes out of web.
const nativePlatform = (): 'ios' | 'android' | 'web' => {
  const platform = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform?.();
  return platform === 'ios' ? 'ios' : platform === 'android' ? 'android' : 'web';
};

export const resolveSentryDsn = (): string => {
  const dev = isDev();
  if (envConfig.IS_NATIVE) {
    if (nativePlatform() === 'ios') {
      return dev ? DSN.iosDev : DSN.iosProd;
    }
    return dev ? DSN.androidDev : DSN.androidProd;
  }
  return dev ? DSN.webDev : DSN.webProd;
};
